/**
 * Founding City Badge — per-city visual theme.
 *
 * As we expand into new markets, each Founding Pro cohort deserves a
 * differentiated visual identity so a "Philadelphia Founding Pro" chip
 * reads distinctly from an "Austin Founding Pro" chip even at a glance.
 *
 * Palette rules:
 *   - Every theme uses `bg-gradient-to-r <from> <to>` for the pill body
 *     so the chip feels celebratory (matches the "you were here first"
 *     framing) without being loud.
 *   - `text`, `border`, `icon` are picked for AA contrast against the
 *     gradient. When adding a new city, run the contrast checker in dev.
 *   - Dark-mode overrides live in the same object because the badge is
 *     shown on both light preloaded-profile pages AND the dark customer
 *     search dropdown.
 *
 * Adding a new city:
 *   1. Add an entry keyed by `city_slug` (as it appears in the DB).
 *   2. If you don't add one, the default palette (`_default`) is used —
 *      no crash, but the chip won't feel distinctive.
 */

const CITY_THEMES = {
  // Austin — warm Texas sunset. Original founding palette.
  austin: {
    label: "Austin",
    gradient: "from-amber-200 to-orange-200",
    darkGradient: "dark:from-amber-500/25 dark:to-orange-500/25",
    border: "border-orange-400",
    text: "text-amber-900",
    darkText: "dark:text-amber-100",
    icon: "text-orange-700",
    darkIcon: "dark:text-amber-300",
  },
  // Philadelphia — Liberty blue → silver. Cool, colonial-founder feel.
  philadelphia: {
    label: "Philadelphia",
    gradient: "from-sky-200 to-indigo-200",
    darkGradient: "dark:from-sky-500/25 dark:to-indigo-500/25",
    border: "border-indigo-400",
    text: "text-indigo-900",
    darkText: "dark:text-indigo-100",
    icon: "text-indigo-700",
    darkIcon: "dark:text-indigo-300",
  },
  // Houston — Gulf Coast teal → forest. Distinct from Austin's warmth.
  houston: {
    label: "Houston",
    gradient: "from-teal-200 to-emerald-200",
    darkGradient: "dark:from-teal-500/25 dark:to-emerald-500/25",
    border: "border-emerald-400",
    text: "text-teal-900",
    darkText: "dark:text-teal-100",
    icon: "text-teal-700",
    darkIcon: "dark:text-teal-300",
  },
  // Dallas — cool slate → rose. Metropolitan, restrained.
  dallas: {
    label: "Dallas",
    gradient: "from-slate-200 to-rose-200",
    darkGradient: "dark:from-slate-500/25 dark:to-rose-500/25",
    border: "border-rose-400",
    text: "text-rose-900",
    darkText: "dark:text-rose-100",
    icon: "text-rose-700",
    darkIcon: "dark:text-rose-300",
  },
  // Nashville — plum → gold. Music-city honky-tonk warmth.
  nashville: {
    label: "Nashville",
    gradient: "from-fuchsia-200 to-yellow-200",
    darkGradient: "dark:from-fuchsia-500/25 dark:to-yellow-500/25",
    border: "border-fuchsia-400",
    text: "text-fuchsia-900",
    darkText: "dark:text-fuchsia-100",
    icon: "text-fuchsia-700",
    darkIcon: "dark:text-fuchsia-300",
  },
  // Central PA — sage → warm cream. Home region — quiet, grounded.
  central_pa: {
    label: "Central PA",
    gradient: "from-lime-200 to-amber-100",
    darkGradient: "dark:from-lime-500/25 dark:to-amber-500/15",
    border: "border-lime-400",
    text: "text-green-900",
    darkText: "dark:text-lime-100",
    icon: "text-green-700",
    darkIcon: "dark:text-lime-300",
  },
  // Outer Banks — ocean cyan → sky. Coastal, beachy, unmistakably OBX.
  "outer-banks-nc": {
    label: "OBX",
    gradient: "from-cyan-200 to-sky-200",
    darkGradient: "dark:from-cyan-500/25 dark:to-sky-500/25",
    border: "border-cyan-400",
    text: "text-cyan-900",
    darkText: "dark:text-cyan-100",
    icon: "text-cyan-700",
    darkIcon: "dark:text-cyan-300",
  },
  // Fallback — matches the original amber palette so unknown cities
  // still look premium, they just don't get city-specific differentiation.
  _default: {
    label: null,   // sentinel — caller must derive from city_slug
    gradient: "from-amber-200 to-orange-200",
    darkGradient: "dark:from-amber-500/25 dark:to-orange-500/25",
    border: "border-orange-400",
    text: "text-amber-900",
    darkText: "dark:text-amber-100",
    icon: "text-orange-700",
    darkIcon: "dark:text-amber-300",
  },
};

/**
 * Return a theme object for the given city slug. Never throws — an
 * unknown slug returns the default palette with `label` derived from
 * the slug so we can still render "SomeNewCity Founding Pro".
 */
export function getFoundingCityTheme(citySlug) {
  const slug = (citySlug || "").toLowerCase().trim();
  if (!slug) return { ...CITY_THEMES._default, label: null };
  if (CITY_THEMES[slug]) return CITY_THEMES[slug];
  // Derive a display label from the slug (e.g. "san-diego" → "San Diego").
  const label = slug
    .split(/[-_]/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return { ...CITY_THEMES._default, label };
}
